/**
 * Landing Page Experimentation Framework
 *
 * Lightweight A/B testing infrastructure for landing page variants.
 * Uses deterministic hashing for consistent variant assignment per user/session.
 *
 * Features:
 * - Deterministic variant assignment (same user always gets same variant)
 * - Weighted variant distribution
 * - Experiment exposure tracking in Mixpanel
 * - Type-safe experiment definitions
 */

import { logger } from '@/shared/lib/logger/logger';
import { trackEvent } from '@/shared/utils/mixpanel';
import type { Experiment, ExperimentAssignment } from './types';

/**
 * Experiment definitions
 *
 * Add new experiments here. Each experiment defines:
 * - id: Unique identifier (used in code)
 * - name: Human-readable name (for Mixpanel)
 * - variants: Array of variant names
 * - weights: Optional weight distribution (default: equal weights)
 * - enabled: Whether experiment is active
 */
export const EXPERIMENTS: Record<string, Experiment> = {
  landing_hero_cta: {
    id: 'landing_hero_cta',
    name: 'Landing Page Hero CTA',
    variants: ['control', 'variant_a', 'variant_b'],
    weights: [0.33, 0.33, 0.34], // Equal distribution
    enabled: true,
  },
  landing_pricing_display: {
    id: 'landing_pricing_display',
    name: 'Landing Page Pricing Display',
    variants: ['control', 'variant_a'],
    weights: [0.5, 0.5], // Equal distribution
    enabled: false, // Disabled by default
  },
} as const;

/**
 * Simple hash function for deterministic variant assignment
 *
 * @param str - String to hash
 * @returns Hash value between 0 and 1
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Normalize to 0-1 range
  return Math.abs(hash) / 2147483647;
}

/**
 * Get user's variant for an experiment
 *
 * Uses deterministic hashing based on userId or sessionId to ensure
 * consistent variant assignment across sessions.
 *
 * @param experimentId - Experiment identifier
 * @param userId - User ID (preferred for consistency)
 * @param sessionId - Session ID (fallback if no userId)
 * @returns Assigned variant name
 */
export function getExperimentVariant(
  experimentId: string,
  userId?: string,
  sessionId?: string
): string {
  const experiment = EXPERIMENTS[experimentId];

  // Return control if experiment doesn't exist or is disabled
  if (!experiment?.enabled) {
    logger.debug('[Experiments] Experiment not found or disabled', {
      component: 'Experiments',
      action: 'get_variant',
      experimentId,
    });
    return 'control';
  }

  // Use userId or sessionId for consistent assignment
  // Fallback to random if neither available (shouldn't happen in production)
  const identifier = userId || sessionId || Math.random().toString();

  // Create unique hash for this experiment + identifier combination
  const hash = simpleHash(identifier + experimentId);

  // Calculate weights (default to equal distribution)
  const weights =
    experiment.weights || experiment.variants.map(() => 1 / experiment.variants.length);

  // Assign variant based on hash and weights
  let cumulative = 0;
  for (let i = 0; i < experiment.variants.length; i++) {
    const weight = weights[i];
    if (weight === undefined) {
      logger.warn('[Experiments] Missing weight for variant', {
        component: 'Experiments',
        action: 'get_variant',
        experimentId,
        variantIndex: i,
      });
      continue;
    }
    cumulative += weight;
    if (hash < cumulative) {
      const variant = experiment.variants[i];
      if (!variant) {
        logger.warn('[Experiments] Missing variant at index', {
          component: 'Experiments',
          action: 'get_variant',
          experimentId,
          variantIndex: i,
        });
        break;
      }
      logger.debug('[Experiments] Variant assigned', {
        component: 'Experiments',
        action: 'get_variant',
        experimentId,
        variant,
        identifier: userId ? 'user' : 'session',
      });
      return variant;
    }
  }

  // Fallback to first variant (shouldn't happen)
  const fallbackVariant = experiment.variants[0];
  if (!fallbackVariant) {
    logger.error('[Experiments] No variants available', undefined, {
      component: 'Experiments',
      action: 'get_variant',
      experimentId,
    });
    return 'control';
  }
  return fallbackVariant;
}

/**
 * Track experiment exposure in Mixpanel
 *
 * Should be called when a user is exposed to an experiment variant.
 * This enables conversion analysis by variant in Mixpanel.
 *
 * @param experimentId - Experiment identifier
 * @param variant - Assigned variant
 * @param userId - User ID (optional)
 */
function trackExperimentExposure(experimentId: string, variant: string, userId?: string): void {
  try {
    trackEvent('Experiment: Exposed', {
      experiment_id: experimentId,
      variant: variant,
      ...(userId && { user_id: userId }),
    });

    logger.debug('[Experiments] Experiment exposure tracked', {
      component: 'Experiments',
      action: 'track_exposure',
      experimentId,
      variant,
    });
  } catch (error) {
    logger.error(
      '[Experiments] Failed to track experiment exposure',
      error instanceof Error ? error : new Error(String(error)),
      {
        component: 'Experiments',
        action: 'track_exposure',
        experimentId,
      }
    );
  }
}

/**
 * Get experiment assignment with tracking
 *
 * Convenience function that both assigns variant and tracks exposure.
 *
 * @param experimentId - Experiment identifier
 * @param userId - User ID (optional, preferred)
 * @param sessionId - Session ID (optional, fallback)
 * @returns Experiment assignment
 */
export function getExperimentAssignment(
  experimentId: string,
  userId?: string,
  sessionId?: string
): ExperimentAssignment {
  const variant = getExperimentVariant(experimentId, userId, sessionId);
  const identifier: string = userId || sessionId || 'anonymous';

  // Track exposure
  trackExperimentExposure(experimentId, variant, userId);

  return {
    experimentId,
    variant,
    identifier,
  };
}

/**
 * Check if specific experiment is enabled
 */
export function isExperimentEnabled(experimentId: string): boolean {
  return EXPERIMENTS[experimentId]?.enabled === true;
}

/**
 * Get list of all enabled experiment IDs
 */
export function getEnabledExperiments(): string[] {
  return Object.values(EXPERIMENTS)
    .filter((exp) => exp.enabled)
    .map((exp) => exp.id);
}
